// @flow

import React, { useLayoutEffect, useEffect, useRef, useState } from 'react';
import styled, { type StyledComponent } from 'styled-components';
// eslint-disable-next-line
import videojsCss from 'video.js/dist/video-js.min.css';
import { timings } from '../../../styles/GlobalStyle';

type TextTrackKind = 'subtitles' | 'captions' | 'descriptions' | 'chapters' | 'metadata';
type TextTrackMode = 'disabled' | 'hidden' | 'showing';

type SourceObject = {|
  src: string,
  type?: string,
|};

interface TrackOptions {
  id?: string;
  kind?: string;
  label?: string;
  language?: string;
}

type TextTrackOptions = {|
  ...TrackOptions,
  kind?: TextTrackKind,
  mode?: TextTrackMode,
  srclang?: string,
  src?: string,
  default?: boolean,
|};

type LanguageTranslations = {|
  [language: string]: string,
|};

type ModalDialogOptions = {|
  content?: any,
  description?: string,
  fillAlways?: boolean,
  label?: string,
  temporary?: boolean,
  uncloseable?: boolean,
|};

type TextTrackSettingsOptions = {|
  ...ModalDialogOptions,
  persistTextTrackSettings?: boolean,
|};

type VolumeControlOptions = {|
  volumeBar?: any,
  vertical?: boolean,
|};

type VolumePanelOptions = {|
  inline?: boolean,
  volumeControl?: VolumeControlOptions,
|};

type ControlBarOptions = {|
  volumePanel?: VolumePanelOptions,
  fullscreenToggle?: boolean,
|};

type VideoPlayerOptions = {|
  aspectRatio?: string,
  autoplay?: boolean | string,
  controlBar?: ControlBarOptions | false,
  textTrackSettings?: TextTrackSettingsOptions,
  controls?: boolean,
  defaultVolume?: number,
  fluid?: boolean,
  height?: number,
  html5?: any,
  inactivityTimeout?: number,
  language?: string,
  languages?: { [code: string]: LanguageTranslations },
  liveui?: boolean,
  loop?: boolean,
  muted?: boolean,
  nativeControlsForTouch?: boolean,
  notSupportedMessage?: string,
  playbackRates?: number[],
  plugins?: { [pluginName: string]: any },
  poster?: string,
  preload?: string,
  sourceOrder?: boolean,
  sources?: SourceObject[],
  src?: string,
  techOrder?: string[],
  tracks?: TextTrackOptions[],
  width?: number,
|};

type VideoPlayerProps = {|
  className?: string,
  options?: VideoPlayerOptions,
|};

const RawVideoPlayer = ({ className, options }: VideoPlayerProps) => {
  const videoNode = useRef(null);
  const [videojs, setVideojs] = useState(null);
  const [initialized, setinitialized] = useState(false);
  const player = useRef();
  const [key, setKey] = useState(0);

  const defaultOptions: VideoPlayerOptions = {
    autoplay: false,
    controls: true,
  };

  useLayoutEffect(() => {
    if (videojs && !initialized) {
      player.current = videojs
        .default(videoNode.current, {
          ...defaultOptions,
          ...options,
        })
        .ready();
      setinitialized(true);
    }
    return () => {
      if (player.current) {
        player.current.dispose();
        setKey(key + 1);
      }
    };
  }, [videojs, options, defaultOptions, key, player, initialized]);

  useEffect(() => {
    if (videojs === null) {
      import('video.js/dist/alt/video.core.novtt').then((response) => {
        setVideojs(response);
      });
    }
  }, [videojs]);

  return (
    <div className={className} key={`video-${key}`}>
      <div data-vjs-player>
        <video ref={videoNode} className="video-js" />
      </div>
    </div>
  );
};

export const VideoPlayer: StyledComponent<VideoPlayerProps, {}, typeof RawVideoPlayer> = styled(
  RawVideoPlayer
)`
  .video-js {
    .vjs-big-play-button {
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      border: none;
      background-image: url('data:image/svg+xml;utf8,<svg width="22px" height="22px" viewBox="0 0 40 40" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><path d="M6.28582615,0.223868073 L35.2230926,18.8263965 C36.0773996,19.3755939 36.0773996,20.6244061 35.2230926,21.1736035 L6.28582615,39.7761319 C5.35731122,40.3730344 4.13617714,39.7063548 4.13617714,38.6025284 L4.13617714,1.39747156 C4.13617714,0.293645248 5.35731122,-0.373034386 6.28582615,0.223868073 Z M6.9265564,3.95299228 L6.9265564,36.0470077 L31.8885684,20 L6.9265564,3.95299228 Z"  fill="white" fill-rule="evenodd"></path></svg>');
      background-position: center;
      background-repeat: no-repeat;
      background-color: transparent;
      background-size: 34px;
      transition: all ${timings.hover};

      .vjs-icon-placeholder:before {
        content: '';
      }
    }
  }

  .video-js:hover .vjs-big-play-button {
    transform: translate(-50%, -50%) scale(1.25);
    background-color: transparent;
    transition: all ${timings.hover};
  }
`;
